import { createFileRoute, redirect } from '@tanstack/react-router'
import Banner from '../../components/Banner'
import { getCurrencySymbol, formatLabel } from '../../imageUrl'
import Breadcrumb from '../../components/Breadcrumb'
import { getFlightByKey, getFlightsTable } from '../../services/API'
import { trackPhoneCallClick } from '../../services/tracking'
import type { FlightFare } from '../../Types'

const FARE_CLASSES = [
  { label: 'Economy',         match: /^economy$/i,          isBusiness: false },
  { label: 'Premium Economy', match: /premium/i,             isBusiness: false },
  { label: 'Business Class',  match: /business/i,            isBusiness: true },
  { label: 'First Class',     match: /first/i,               isBusiness: true },
]

function cheapestFare(fares: FlightFare[], match: RegExp): FlightFare | undefined {
  return fares
    .filter(f => f.adv_class && match.test(f.adv_class))
    .sort((a, b) => (a.adv_total ?? 0) - (b.adv_total ?? 0))[0]
}

export const Route = createFileRoute('/$level1/$level2')({
  loader: async ({ params }) => {
    const key = `/flights/${params.level1}/${params.level2}`
    const mapsKey = `/flights/${params.level2}`
    const byKey = await getFlightByKey(key, mapsKey)
    if (byKey.flights.length === 0) {
      throw redirect({ to: '/$level1', params: { level1: params.level1 } })
    }
    const cityName = byKey.flights[0]?.adv_dest ?? params.level2.replace(/-/g, ' ')
    const continentName = byKey.flights[0]?.adv_cont ?? params.level1.replace(/-/g, ' ')
    const tableData = cityName ? await getFlightsTable(cityName) : { flightsArray: [], phoneNumbers: [] }
    return { byKey, tableData, cityName, continentName }
  },
  component: Level2Page,
})

function Level2Page() {
  const { level1 } = Route.useParams()
  const { byKey, tableData, cityName, continentName } = Route.useLoaderData()
  const phoneLookup = window.siteVar?.phoneLookup?.flat()
  const currencySymbol = getCurrencySymbol()
  const bannerImage = byKey.destination?.ImageLink ?? ''
  const ecoPhone = phoneLookup?.[0]?.phoneNumber ?? ''
  const busPhone = phoneLookup?.[1]?.phoneNumber ?? ecoPhone

  const allFares = tableData.flightsArray.flatMap(e => e.fares)
  const allBusiness = allFares.length > 0 && allFares.every(f => /business|first/i.test(f.adv_class ?? ''))
  const introPhone = allBusiness ? busPhone : ecoPhone

  return (
    <div className="d-flex flex-column container-fluid p-0">
      <title>{formatLabel(cityName)} Flights | Trailfinders</title>
      <Breadcrumb
        items={[
          { label: continentName, to: '/$level1/', params: { level1 } },
          { label: cityName },
        ]}
      />

      <Banner title={`Flights to ${formatLabel(cityName)}`} image={bannerImage} />

      <section className="container-fluid pb-5 bg-light">
        <div className="container py-5 bg-white rounded-3 shadow-sm position-relative" style={{ marginTop: '-90px', zIndex: 1 }}>

          {/* Intro */}
          <div className="row justify-content-center mb-5">
            <div className="col-12 col-md-8 text-center">
              <h6>
                Looking for {cityName} flights?<br />
                Trailfinders has offered unbeatable value and award-winning service on flight bookings for over 50 years.<br />
                {introPhone && (
                  <>Give us a call on <a href={`tel:${introPhone}`} className="text-warning fw-bold" onClick={trackPhoneCallClick} data-ga4-label={allBusiness ? 'first_&_business_class_travel' : 'tailormade_travel_worldwide'} data-ga4-category="text_link">{introPhone}</a> and speak to one of our Travel Experts today.</>
                )}
              </h6>
            </div>
          </div>

          {/* Fare grid */}
          {tableData.flightsArray.map((entry, i) => (
            <div key={i} className="row align-items-stretch g-2 mb-3">

              {/* Airport label */}
              <div className="col-12 col-md-2 d-flex align-items-center gap-2">
                <i className="icon-flight_take_off glyph-md text-muted" />
                <div>
                  <div className="text-muted small">Departure Airport:</div>
                  <div className="fw-bold">{entry.loc}</div>
                </div>
              </div>

              {/* Class columns */}
              {FARE_CLASSES.map(cls => {
                const fare = cheapestFare(entry.fares, cls.match)
                const centre = entry.phonedata?.[0]
                const phone = cls.isBusiness
                  ? (centre?.add_sdtel ?? busPhone)
                  : (centre?.add_tel ?? ecoPhone)
                return (
                  <div key={cls.label} className="col-6 col-md">
                    <div className="bg-dark text-white text-center p-3 h-100 d-flex flex-column justify-content-center">
                      <div className="small mb-1">{cls.label}</div>
                      {fare?.adv_total ? (
                        <>
                          <div className="small text-muted">Return Flight From</div>
                          <div className="fares-board-yellow d-flex align-items-baseline justify-content-center gap-1">
                            <p className="mb-0">{currencySymbol}</p>
                            <span className="fares-board" style={{ fontSize: '3.5em' }}>{fare.adv_total}</span>
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="small text-muted">Call For Latest Offers</div>
                          <p className="fares-board-yellow mb-0 fw-bold">{phone}</p>
                        </>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          ))}

          {/* Footer: Consumer Protection + Holiday inspiration */}
          <div className="row g-3 mt-4">
            <div className="col-12 col-lg-6">
              <div className="bg-dark text-white p-4 rounded h-100">
                <h6 className="text-warning">Consumer Protection</h6>
                <ul className="small mb-0">
                  <li>No hidden extras — Trailfinders prices include all fuel surcharges and pre-paid taxes.</li>
                  <li>Trailfinders offers unrivalled consumer protection: all monies paid to Trailfinders are held in a Trust Account, protecting your funds in the unlikely event of our insolvency.</li>
                  <li>All flights are also covered by the Trailfinders Guarantee which guarantees to make good any money paid to us in the event of the financial collapse of an airline. This pledge has been honoured since our foundation. No client has suffered financial loss or been left wanting.</li>
                </ul>
              </div>
            </div>
            <div className="col-12 col-lg-6">
              <div className="bg-dark text-white p-4 rounded h-100 d-flex flex-column justify-content-center align-items-center text-center">
                <h6 className="text-warning">Looking for holiday inspiration?</h6>
                <p className="small">Take a look at our {continentName} Holidays pages</p>
                <a href={`/holidays/${level1}`} className="btn btn-warning rounded-0 px-4">
                  {continentName} Holidays
                </a>
              </div>
            </div>
          </div>

        </div>
      </section>
    </div>
  )
}
